import { useListAdminStandardDocumentsQuery } from 'API/order/documents/DocumentApi';
import React, { useState } from 'react';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { PDFPreview } from 'ui-toolkit/components/PDFPreview';
import { createDownloadDocumentUrl, mergeDocumentsAsync } from 'API/order/documents/adminDocumentsAPI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLoader, faPrint } from '@fortawesome/pro-regular-svg-icons';
import { createPortal } from 'react-dom';
import { Form, Formik } from 'formik';
import { MergeDocumentModal } from 'components/order/documentsPortal/standardDocuments/MergeDocumentModal';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import { useListDocumentCategoriesQuery } from 'API/documentCategories/DocumentCategoriesApi';

export interface IPreviewContainerProps {
  documentIds?: string[];
  orderId: string;
  target?: string;
}

export const PreviewContainer: React.FC<IPreviewContainerProps> = ({ orderId, target, documentIds }) => {
  const { data } = useListDocumentCategoriesQuery();
  const category = data?.activeCategories.find((t) => t.name === 'package-all-documents-for-all-disbursed-checks');
  const [isDocsLoading, setIsDocsLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<IStandardDocumentVersionData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { data: adminDocuments } = useListAdminStandardDocumentsQuery({ orderId });

  const documents = adminDocuments?.filter((d) => documentIds?.includes(d.id));

  const printSelectedDocuments = async (documentIds: string[]) => {
    setIsDocsLoading(true);
    try {
      const result = await mergeDocumentsAsync(orderId, documentIds, 'All documents', {
        name: 'Other',
      } as IDocumentCategory);
      setPreviewDoc({
        id: result['data'].standardDocumentId,
        versionId: result['data'].id,
      } as IStandardDocumentVersionData);
    } finally {
      setIsDocsLoading(false);
    }
  };

  const toggleModal = (value: boolean) => setShowModal(value);

  const mergeDocumentsFetchStatus = useSelector(
    (state: RootStore) => state.order.documents.adminDocuments.mergeDocumentsFetchStatus
  );

  const renderModal = (modalComponent: React.ReactNode, target: string | undefined) =>
    target ? createPortal(modalComponent, document.getElementById(target)!) : modalComponent;

  return (
    <>
      {!!documentIds?.length && (
        <ButtonTW disabled={isDocsLoading} onClick={toggleModal.bind(this, true)} data-test-id={'print-all-docs'}>
          <FontAwesomeIcon icon={isDocsLoading ? faLoader : faPrint} /> Print all docs for Checks
        </ButtonTW>
      )}
      {showModal && (
        <Formik
          initialValues={{
            selectedDocuments: documents ?? [],
            category,
            fileName: 'package-generated-on-' + new Date().toISOString(),
          }}
          onSubmit={async (values) => {
            const result = await printSelectedDocuments(values.selectedDocuments.map((d) => d.id));
            setShowModal(false);
            setPreviewDoc({
              id: result['data'].standardDocumentId,
              versionId: result['data'].id,
            } as IStandardDocumentVersionData);
          }}
        >
          {() => (
            <Form>
              {renderModal(
                <MergeDocumentModal
                  fetchError={'There was an error combining documents.'}
                  fetchStatus={mergeDocumentsFetchStatus}
                  onClose={toggleModal.bind(this, false)}
                />,
                target
              )}
            </Form>
          )}
        </Formik>
      )}
      {previewDoc &&
        renderModal(
          <PDFPreview
            printOnLoad
            file={createDownloadDocumentUrl(previewDoc.id, previewDoc.versionId)}
            onClose={() => setPreviewDoc(null)}
          />,
          target
        )}
    </>
  );
};
